/**
 * Fila de promociones bajo el carrusel (ADM-05): un bloque ancho y dos
 * angostos, de lado a lado de la pantalla.
 *
 * Cada bloque es un pequeno carrusel propio: el primero rota entre las piezas
 * 1, 4, 7…; el segundo entre 2, 5, 8…; el tercero entre 3, 6, 9…. Asi el
 * administrador carga las piezas en orden y la fila se arma sola; con tres
 * piezas o menos nada rota y cada bloque muestra la suya.
 *
 * Las piezas anchas van al primer bloque y las angostas a los otros dos: el
 * ancho lo decide la posicion, no la imagen, y por eso el panel avisa que en
 * esta fila se alternan una pieza ancha y dos angostas.
 */
import type { Banner } from '@elcarpintero/compartido';

import { useRotacion } from '../../hooks/use-rotacion';
import { LaminaBanner } from './LaminaBanner';

/** Bloques de la fila: el primero ancho, los otros dos angostos. */
const BLOQUES = 3;
/** Cuanto se queda cada pieza; distinto del carrusel para que no cambien todos a la vez. */
const INTERVALO_MS = 7500;

export interface FilaPromocionesProps {
  banners: Banner[];
}

export function FilaPromociones({ banners }: FilaPromocionesProps) {
  if (banners.length === 0) return null;

  const bloques = Array.from({ length: BLOQUES }, (_, bloque) =>
    banners.filter((_, indice) => indice % BLOQUES === bloque),
  ).filter((piezas) => piezas.length > 0);

  return (
    <ul className="promociones" aria-label="Promociones destacadas">
      {bloques.map((piezas, bloque) => (
        <li key={bloque} className={bloque === 0 ? 'promocion ancha' : 'promocion'}>
          <BloqueRotativo
            piezas={piezas}
            tamanos={bloque === 0 ? TAMANOS_PROMOCION_ANCHA : TAMANOS_PROMOCION}
            prioridad={bloque === 0}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * Un bloque ancho y dos angostos (2fr 1fr 1fr); en el telefono el ancho ocupa la
 * fila y los angostos van de a dos debajo (`carrusel.css`). El script de
 * `index.html` precarga la primera pieza ancha con este mismo valor.
 */
const TAMANOS_PROMOCION_ANCHA = '(max-width:720px) 100vw, 670px';
const TAMANOS_PROMOCION = '(max-width:720px) 50vw, 335px';

interface BloqueRotativoProps {
  piezas: Banner[];
  tamanos: string;
  /**
   * En el teléfono la pieza ancha ocupa la pantalla entera y se ve más grande
   * que el carrusel (que muestra la suya entera, achicada): es el LCP de la
   * portada (PageSpeed, 04-10-2026). Su primera pieza no se difiere.
   */
  prioridad?: boolean;
}

function BloqueRotativo({ piezas, tamanos, prioridad = false }: BloqueRotativoProps) {
  const { actual, manejadores } = useRotacion(piezas.length, INTERVALO_MS);

  return (
    <div className="promocion-pista" {...manejadores}>
      {piezas.map((banner, indice) => (
        <div
          key={banner.id}
          className={indice === actual ? 'promocion-lamina activa' : 'promocion-lamina'}
          aria-hidden={indice !== actual}
          inert={indice !== actual}
        >
          <LaminaBanner banner={banner} tamanos={tamanos} prioridad={prioridad && indice === 0} />
        </div>
      ))}
    </div>
  );
}
